import React, { createContext, useContext, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { ServiceType } from '@/finsight/domain/ServiceType';
import ContactRepository from '@/finsight/infrastructure/repository/ContactRepository';
import { useDIContext } from '@/Framework/DI/DIContext';
import messages from '@/Framework/Message/messages';
import { IRequestServicePayload } from '@/finsight/domain/vo/IRequestServicePayload';
import { AlertManager } from '@dealroadshow/uikit/core/components/Alert';
import { getErrorMessage } from '@/Framework/Message/Mapper/getMessage';
import { IRequestServiceData } from '@/finsight/ui/common/components/RequestServiceModal/IRequestServiceData';
import config from './modalConfig';

interface IRequestServiceModalState {
  isOpen: boolean,
  titleModal: string | React.ReactNode,
  titleForm: string | React.ReactNode,
  recaptcha: ReCAPTCHA | null,
  serviceType: ServiceType | null,
  isCaptcha: boolean,
  isFetching: boolean,
}

const INITIAL_STATE: IRequestServiceModalState = {
  isOpen: false,
  titleModal: '',
  titleForm: '',
  recaptcha: null,
  serviceType: null,
  isCaptcha: true,
  isFetching: false,
};

const useRequestServiceModal = () => {
  const [state, setState] = useState<IRequestServiceModalState>(INITIAL_STATE);
  const { container } = useDIContext();
  const contactRepository = container.get(ContactRepository);

  const openModal = (serviceType: ServiceType) => {
    setState((prevState) => ({
      ...prevState, isOpen: true, serviceType, ...config[serviceType],
    }));
  };

  const closeModal = () => {
    setState(INITIAL_STATE);
  };

  const setRecaptchaObject = (recaptcha: ReCAPTCHA) => {
    setState((prevState) => ({ ...prevState, recaptcha }));
  };

  const requestService = async (payload: IRequestServicePayload) => {
    try {
      setState((prevState) => ({ ...prevState, isFetching: true }));
      await contactRepository.requestService(payload);
      AlertManager.success(messages.FINSIGHT_PRICING_REQUEST_SERVICE_SUCCESS);
      closeModal();
    } catch (error) {
      AlertManager.error(getErrorMessage(error));
    } finally {
      setState((prevState) => ({ ...prevState, isFetching: false }));
    }
  };

  const onSubmit = async (data: IRequestServiceData) => {
    const payload = {
      firstName: data.firstName,
      lastName: data.lastName,
      companyName: data.companyName,
      corpEmail: data.email,
      phone: data.phone.value,
      serviceType: state.serviceType,
      comment: data.comment,
      token: null,
    };

    if (!state.isCaptcha) {
      await requestService(payload);
    } else if (state.recaptcha.getValue()) {
      payload.token = state.recaptcha.getValue();
      await requestService(payload);
    } else {
      payload.token = await state.recaptcha.executeAsync();
      await requestService(payload);
    }
  };

  return {
    ...state,
    openModal,
    closeModal,
    setRecaptchaObject,
    onSubmit,
  };
};

type RequestServiceModalContextType = ReturnType<typeof useRequestServiceModal>;

export const RequestServiceModalContext = createContext<RequestServiceModalContextType>(null);

export const useRequestServiceModalContext = () => {
  const context = useContext<RequestServiceModalContextType>(RequestServiceModalContext);

  if (!context) {
    throw new Error('useRequestServiceModalContext must be used within a RequestServiceModalContext');
  }

  return context;
};

interface IProps {
  children: React.ReactNode,
}

const RequestServiceModalProvider = ({ children }: IProps) => (
  <RequestServiceModalContext.Provider value={ useRequestServiceModal() }>
    { children }
  </RequestServiceModalContext.Provider>
);

export default RequestServiceModalProvider;
